import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { BusinessProvider, useBusiness } from "@/context/BusinessContext";
import { AuthCallback } from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Translator from "@/pages/Translator";
import Inbox from "@/pages/Inbox";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Plans from "@/pages/Plans";
import Onboarding from "@/pages/Onboarding";
import Learning from "@/pages/Learning";
import { LearningProvider } from "@/context/LearningContext";
import { PhrasePopup } from "@/components/PhrasePopup";

const Spinner = () => (
  <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>
);

function Protected({ children, business }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (business && user.role !== "manager" && user.role !== "agent") return <Navigate to="/app" replace />;
  return children;
}

// Business pages require the business to have completed onboarding.
function BusinessGate({ children }) {
  const { business, loading } = useBusiness();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (business && !business.onboarded && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) {
    if (user.role === "manager" || user.role === "agent") return <Navigate to="/inbox" replace />;
    return <Navigate to="/app" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/app" element={<Protected><Translator /></Protected>} />
      <Route path="/onboarding" element={<Protected business><Onboarding /></Protected>} />
      <Route path="/inbox" element={<Protected business><BusinessGate><Inbox /></BusinessGate></Protected>} />
      <Route path="/analytics" element={<Protected business><BusinessGate><Analytics /></BusinessGate></Protected>} />
      <Route path="/learning" element={<Protected business><BusinessGate><Learning /></BusinessGate></Protected>} />
      <Route path="/settings" element={<Protected business><BusinessGate><Settings /></BusinessGate></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BusinessProvider>
          <BrowserRouter>
            <LearningProvider>
              <AppRouter />
              <PhrasePopup />
              <Toaster position="top-center" richColors />
            </LearningProvider>
          </BrowserRouter>
        </BusinessProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
